import { Switch } from '@headlessui/react'
import StepperInput from 'components/Stepper'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useEffect, useState } from 'react'
import { LimitType, mapSchedulingLimitFromDTO, mapSchedulingLimitToDTO, SchedulingLimit, SchedulingRestrictionsDto } from 'types/scheduling-restrictions'
import { classNames } from 'utils/dom-helpers'
import {
    deleteApiRequest,
    getApiRequest,
    postApiRequest,
    putApiRequest,
    useFetch,
} from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { FetchMethods } from 'utils/types'
import BookingLimit from './BookingLimit'
import { EditIcon } from 'utils/custom-icons'

export default function SchedulingLimits({
    restriction_template,
    local_restriction,
    is_global = false,
    show_scheduling_limits = false,
    cancel_label = 'cancel',
    onCancel = () => { },
    onSave = () => { },
}: {
    restriction_template: SchedulingRestrictionsDto
    local_restriction?: SchedulingRestrictionsDto
    is_global?: boolean
    cancel_label?: string
    show_scheduling_limits?: boolean
    onCancel?(): void
    onSave?(): void
}) {
    const { translations } = useAppContext()
    const { setGlobalError } = useAppErrorApiContext()
    const { GlobalSchedulingLimitsModal, StaffModal, ServiceModal } =
        useAuth()

    const { tenant } = useTenantContext()
    const { attributes, setAttributes } = GlobalSchedulingLimitsModal

    const [use_global_limits, toggleGlobalLimits] = useState<boolean>(true)
    const [title_color, setTitleColor] = useState<string>(
        'text-secondary-blue'
    )

    const [restriction, setRestriction] =
        useState<SchedulingRestrictionsDto>(restriction_template)
    const [global_restriction_dto, setGlobalRestrictionDto] =
        useState<SchedulingRestrictionsDto>(restriction_template)
    const [restriction_dto, setRestrictionDto] =
        useState<SchedulingRestrictionsDto>(restriction_template)
    const [enable_limits, toggleEnableLimits] = useState<boolean>(false)
    const [scheduling_limits, setSchedulingLimits] = useState<SchedulingLimit>({
        type: restriction_template.type as LimitType,
        lookup_id: restriction_template.lookupId
    })

    const {
        data: data_global_restrictions,
        is_loading,
        doFetch: getGlobalRestrictions,
    } = useFetch(
        `/v1/scheduling-restrictions/by-tenant-id?tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!tenant?.id
    )

    async function fetchRestrictions(lookup_id: string, is_staff: boolean) {
        try {
            let res = await getApiRequest(`/v1/scheduling-limits/by-${is_staff ? "staff" : "service"}-id?${is_staff ? "staffId" : "serviceId"}=${lookup_id}`)
            if (res?.ok == false) {
                throw res
            }
            setSchedulingLimits(mapSchedulingLimitFromDTO(res))
            toggleEnableLimits(!!res)
        } catch (e) {
            toggleEnableLimits(false)
        }
    }

    useEffect(() => {
        if (restriction_template.lookupId) {
            fetchRestrictions(restriction_template.lookupId, restriction_template.type == LimitType.STAFF)
        }
    }, [restriction_template])

    useEffect(() => {
        if (use_global_limits) {
            setTitleColor('text-gray-300')
        } else {
            setTitleColor('text-primary-light')
        }
    }, [use_global_limits])

    useEffect(() => {
        if (attributes?.refetch && !is_loading) {
            getGlobalRestrictions()
            setAttributes({
                ...attributes,
                refetch: false,
            })
        }
    }, [attributes, setAttributes])

    useEffect(() => {
        if (data_global_restrictions?.id) {
            let global_restriction = mapRestriction(
                data_global_restrictions
            )
            setGlobalRestrictionDto(
                global_restriction || restriction_template
            )
        }
    }, [data_global_restrictions])

    useEffect(() => {
        if (is_global) {
            let globa_restriction =
                global_restriction_dto || restriction_template
            toggleGlobalLimits(false)
            setRestriction(globa_restriction)
            setRestrictionDto(globa_restriction)
        } else if (local_restriction?.id) {
            let restrictions = mapRestriction(local_restriction)
            toggleGlobalLimits(false)
            setRestriction(restrictions)
            setRestrictionDto(restrictions)
        } else {
            let new_restriction = {
                ...global_restriction_dto,
                tenantId: tenant?.id as string,
                serviceIds: restriction_template.serviceIds,
                staffId: restriction_template.staffId,
                type: restriction_template.type,
                lookupId: restriction_template.lookupId,
            }
            delete new_restriction.id
            toggleGlobalLimits(true)
            setRestriction(new_restriction)
            setRestrictionDto(new_restriction)
        }
    }, [local_restriction, global_restriction_dto])

    function mapRestriction(restrictionDTO: SchedulingRestrictionsDto) {
        let restriction: SchedulingRestrictionsDto = {
            ...restrictionDTO,
        }
        restriction.bookMinBeforeStartTime /= 60
        restriction.cancelMinBeforeStartTime /= 60
        restriction.reschedMinBeforeStartTime /= 60
        return restriction
    }

    const handleUseGlobalToggle = function (v: boolean) {
        toggleGlobalLimits(v)
        if (v) {
            setRestriction(global_restriction_dto)
        } else {
            setRestriction(restriction_dto)
        }
    }

    const handleEditGlobalClick = function () {
        GlobalSchedulingLimitsModal.open()
    }

    const handleSaveClick = async function () {
        try {
            let response
            if (use_global_limits) {
                if (restriction_dto?.id) {
                    response = await deleteApiRequest(
                        `/v1/scheduling-restrictions/${restriction_dto.id}`
                    )
                }
                setRestrictionDto(global_restriction_dto)
            } else {
                let restrictionDTO = {
                    ...restriction,
                }
                restrictionDTO.bookMinBeforeStartTime *= 60
                restrictionDTO.cancelMinBeforeStartTime *= 60
                restrictionDTO.reschedMinBeforeStartTime *= 60

                if (restriction_dto?.id) {
                    response = await putApiRequest(
                        `/v1/scheduling-restrictions/${restriction_dto.id}`,
                        restrictionDTO
                    )
                } else {
                    response = await postApiRequest(
                        `/v1/scheduling-restrictions`,
                        restrictionDTO
                    )
                }
                setRestrictionDto(response)
            }

            if (response && response.ok) {
                console.info('Successfully updated scheduling limits')
            }

            if (enable_limits) {
                let payload = mapSchedulingLimitToDTO(scheduling_limits)
                let res
                if (scheduling_limits?.id) {
                    res = await putApiRequest(`/v1/scheduling-limits/${scheduling_limits.id}`, payload)
                } else {
                    res = await postApiRequest("/v1/scheduling-limits", payload)
                }
            } else if (scheduling_limits?.id) {
                let res = await deleteApiRequest(`/v1/scheduling-limits/${scheduling_limits.id}`)
            }

            onSave()
        } catch (e) {
            setGlobalError(e)
        }
    }

    return (
        <div className='w-full'>
            <div
                className={classNames(
                    'flex justify-between mb-2',
                    is_global ? 'hidden' : ''
                )}
            >
                <Translation
                    render_as="label"
                    className="text-primary text-xl font-medium font-display"
                    content_key="scheduling_limits"
                />

                <div className="flex gap-3 items-center">
                    <Switch
                        name="use_global_limits"
                        id="use_global_limits"
                        checked={use_global_limits}
                        onChange={handleUseGlobalToggle}
                        className={`${use_global_limits
                            ? 'bg-primary-light'
                            : 'bg-gray-300'
                            }
                        block relative inline-flex flex-shrink-0 h-5 w-10 
                        border-2 border-transparent rounded-full cursor-pointer 
                        transition-colors ease-in-out duration-200 
                        focus:outline-none focus-visible:ring-2 
                        focus-visible:ring-white focus-visible:ring-opacity-75`}
                    >
                        <Translation
                            className="sr-only"
                            content_key="use_global_limits_label"
                            render_as="span"
                        />
                        <span
                            aria-hidden="true"
                            className={`${use_global_limits
                                ? 'translate-x-5'
                                : 'translate-x-0'
                                }
                            pointer-events-none inline-block h-4 w-4 
                            rounded-full bg-white shadow-lg transform ring-0 
                            transition ease-in-out duration-200`}
                        />
                    </Switch>
                        <Translation
                            render_as="label"
                            className="text-gray-500 text-base"
                            content_key="use_global_limits_label"
                        />
                        <div
                            onClick={handleEditGlobalClick}
                            title={ getTranslation('edit_global_limits_label',
                                                translations) }
                        >
                            <EditIcon/>
                        </div>
                </div>
            </div>
            <Translation
                render_as="div"
                className="text-basis font-basis text-gray-400 mb-5"
                content_key="scheduling_limits_subtitle"
            />
            <div
                className={
                    use_global_limits
                        ? 'opacity-70 text-gray-400'
                        : 'opacity-100 text-gray-900'
                }
            >
                <Translation
                    render_as="div"
                    className={`${title_color} text-xl font-medium font-display mb-4`}
                    content_key="booking"
                />

                <div className="lg:flex items-center justify-between gap-5 bg-gray-100 rounded-lg  pb-6 mb-6">
                    <div
                        className={classNames(
                            'flex flex-col p-4 mb-4 lg:mb-0 w-full lg:w-96 text-base'
                        )}
                    >
                        <Translation
                            render_as="p"
                            content_key="set_min_hours_booked"
                        />
                        <br></br>

                        <p className="mb-1">
                            <Translation
                                render_as="p"
                                content_key="minimum_hours"
                            />
                        </p>

                        <StepperInput
                            default_value={
                                restriction?.bookMinBeforeStartTime as number
                            }
                            disabled={use_global_limits}
                            max_value={999}
                            helper_text={getTranslation(
                                'hours',
                                translations
                            )}
                            onChange={(v: number) => {
                                if (restriction) {
                                    setRestriction({
                                        ...restriction,
                                        bookMinBeforeStartTime: v,
                                    })
                                }
                            }}
                        />
                    </div>
                    <div
                        className=" h-px lg:h-32 w-full lg:w-px "
                        style={{
                            border: '1px solid silver',
                        }}
                    ></div>

                    <div
                        className={classNames(
                            'flex flex-col p-4 w-full lg:w-96 text-base'
                        )}
                    >
                        <Translation
                            render_as="p"
                            content_key="set_max_days_booked"
                        />

                        <br></br>
                        <p className="mb-1 ">
                            <Translation
                                render_as="p"
                                content_key="maximum_days"
                            />
                        </p>
                        <StepperInput
                            default_value={
                                restriction?.bookMaxDaysInAdvance as number
                            }
                            disabled={use_global_limits}
                            max_value={999}
                            helper_text={getTranslation(
                                'days',
                                translations
                            )}
                            onChange={(v: number) => {
                                if (restriction) {
                                    setRestriction({
                                        ...restriction,
                                        bookMaxDaysInAdvance: v,
                                    })
                                }
                            }}
                        />
                    </div>
                </div>

                <hr />

                {show_scheduling_limits && //TODO: add support for global limits
                    <>
                        <BookingLimit
                            enable_limits={enable_limits}
                            toggleEnableLimits={toggleEnableLimits}
                            scheduling_limits={scheduling_limits}
                            setSchedulingLimits={setSchedulingLimits}
                        />
                        <hr />
                    </>
                }

                <div className="lg:flex items-start justify-between gap-5 my-5 ">
                    <div
                        className={classNames(
                            'flex flex-col w-full lg:w-96 '
                        )}
                    >
                        <Translation
                            render_as="div"
                            className={`${title_color} text-xl font-medium font-display mb-3`}
                            content_key="cancellation"
                        />
                        <div className="flex items-center text-base mb-2 gap-1 ">
                            <input
                                type="checkbox"
                                className={`h-6 w-6 mr-2 border-gray-300 rounded ${title_color} focus:ring-primary-light`}
                                disabled={use_global_limits}
                                checked={restriction.canCancel as boolean}
                                onChange={() => {
                                    if (restriction) {
                                        setRestriction({
                                            ...restriction,
                                            canCancel:
                                                !restriction.canCancel,
                                        })
                                    }
                                }}
                            />

                            <Translation
                                render_as="span"
                                content_key="allow_client_cancelling"
                            />
                        </div>
                        <p className="text-base">
                            <Translation
                                render_as="p"
                                content_key="set_minimum_hours_cancelled"
                            />{' '}
                        </p>
                        <br></br>
                        <p className="mb-1 text-base">
                            <Translation
                                render_as="p"
                                content_key="minimum_hours"
                            />
                        </p>
                        <StepperInput
                            default_value={
                                restriction?.cancelMinBeforeStartTime as number
                            }
                            disabled={use_global_limits}
                            max_value={999}
                            helper_text={getTranslation(
                                'hours',
                                translations
                            )}
                            onChange={(v: number) => {
                                if (restriction) {
                                    setRestriction({
                                        ...restriction,
                                        cancelMinBeforeStartTime: v,
                                    })
                                }
                            }}
                        />
                    </div>
                    <div
                        className={classNames(
                            'flex flex-col w-full lg:w-96'
                        )}
                    >
                        <Translation
                            render_as="div"
                            className={`${title_color} text-xl font-medium font-display mb-3`}
                            content_key="rescheduling"
                        />
                        <div className="flex items-center text-base mb-2 gap-1">
                            <input
                                type="checkbox"
                                className={`h-6 w-6 mr-2 border-gray-300 rounded ${title_color} focus:ring-primary-light`}
                                disabled={use_global_limits}
                                checked={restriction.canResched as boolean}
                                onChange={() => {
                                    if (restriction) {
                                        setRestriction({
                                            ...restriction,
                                            canResched:
                                                !restriction.canResched,
                                        })
                                    }
                                }}
                            />
                            <Translation
                                render_as="span"
                                content_key="allow_client_reschedule"
                            />
                        </div>
                        <p className="text-base">
                            <Translation
                                render_as="p"
                                content_key="set_minimum_hours_reschedule"
                            />{' '}
                        </p>

                        <br></br>
                        <p className="mb-1 text-base">
                            <Translation
                                render_as="p"
                                content_key="minimum_hours"
                            />
                        </p>
                        <StepperInput
                            default_value={
                                restriction?.reschedMinBeforeStartTime as number
                            }
                            disabled={use_global_limits}
                            max_value={999}
                            helper_text={getTranslation(
                                'hours',
                                translations
                            )}
                            onChange={(v: number) => {
                                if (restriction) {
                                    setRestriction({
                                        ...restriction,
                                        reschedMinBeforeStartTime: v,
                                    })
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="flex mt-auto justify-end gap-3">
                <div className="flex justify-start gap-3">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg  inline-block btn-gap"
                        onClick={() => {
                            if (
                                ServiceModal.is_open &&
                                !GlobalSchedulingLimitsModal.is_open
                            ) {
                                ServiceModal.close()
                            }
                            if (
                                StaffModal.is_open &&
                                !GlobalSchedulingLimitsModal.is_open
                            )
                                StaffModal.close()
                            if (
                                GlobalSchedulingLimitsModal.is_open ||
                                StaffModal.is_open ||
                                ServiceModal.is_open
                            ) {
                                GlobalSchedulingLimitsModal.close()
                            }
                        }}
                    >
                        <Translation content_key="cancel" />
                    </button>
                </div>
                <div>
                    <button
                        type="button"
                        onClick={handleSaveClick}
                        className={classNames(
                            'group relative flex justify-center',
                            'btn-gap border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                    >
                        <Translation content_key="save" />
                    </button>
                </div>
            </div>
        </div>
    )
}
